/**
 * Declarative configuration for Supabase real-time subscriptions
 * This replaces ~200 lines of repetitive subscription setup in AppStack.js
 */

import { type TableName } from '@atlas/supabase-types';

// Target types for where data should be stored
export type AtomTarget = {
  type: 'atom';
  setter: string;
};

export type ConstantsTarget = {
  type: 'constants';
  key: string;
  asDict?: boolean; // Keep as dict instead of converting to array
};

export type ConstantsSingleTarget = {
  type: 'constantsSingle';
  key: string;
  // For single-document constants (e.g., loyaltySettings) - spreads fields directly
};

export type SplitTarget = {
  type: 'split';
  splitBy: string;
  targets: Record<string, string>; // value -> setter mapping
};

export type ConstantsSplitTarget = {
  type: 'constantsSplit';
  splitBy: string;
  keys: Record<string, string>; // value -> constants key mapping
};

export type ClearOnlyTarget = {
  type: 'clearOnly';
  setter: string;
};

export type SubscriptionTarget =
  | AtomTarget
  | ConstantsTarget
  | ConstantsSingleTarget
  | SplitTarget
  | ConstantsSplitTarget
  | ClearOnlyTarget;

export interface SubscriptionConfig {
  // Table configuration
  table?: TableName; // Defaults to snake_case of key
  filterField?: 'organization_id' | 'firestore_id'; // Default: organization_id
  deleteKey?: 'firestoreId' | 'storeId'; // Field to use when deleting from state

  // Target configuration
  target: SubscriptionTarget;

  // Data transformations (client-side only - backend handles most transforms)
  addLocalDate?: boolean; // Add localDate based on store timezone (client-side)
  toArray?: boolean; // Convert dict to array before setting

  // Behavior modifiers
  debounce?: number; // Debounce ms for individual refetch (products)
  fetchSingle?: boolean; // Fetch single item instead of all (products)
  onUpdate?: string; // Callback name to run after update (stores -> resubscribeDailyStats)
  triggerProductRefetch?: boolean; // For stock updates: trigger product refetch instead of direct update

  // Transform function key
  transformKey?: string; // Reference to named transform function
}

/**
 * All subscription configurations
 * Key = entity name (used for logging and channel naming)
 * Config = how to set up and handle the subscription
 */
export const SUBSCRIPTION_CONFIGS: Record<string, SubscriptionConfig> = {
  // === SIMPLE ATOM SUBSCRIPTIONS ===
  roles: {
    table: 'roles',
    target: { type: 'atom', setter: 'setRoles' },
  },

  productCategories: {
    table: 'product_categories',
    target: { type: 'atom', setter: 'setProductCategories' },
  },

  suppliers: {
    table: 'suppliers',
    target: { type: 'atom', setter: 'setSuppliersDict' },
  },

  productUnits: {
    table: 'organization_product_units',
    target: { type: 'atom', setter: 'setProductUnitsDict' },
  },

  // === CONSTANTS SUBSCRIPTIONS ===
  productModifiers: {
    table: 'product_modifiers',
    target: { type: 'constants', key: 'productModifiers' },
  },

  // === DEBOUNCED SINGLE-ITEM REFETCH ===
  products: {
    table: 'products',
    target: { type: 'atom', setter: 'setProductsDict' },
    debounce: 2000,
    fetchSingle: true,
  },

  // === WITH SECONDARY CALLBACK ===
  stores: {
    table: 'stores',
    target: { type: 'atom', setter: 'setStores' },
    addLocalDate: true, // Client-side: adds localDate based on store timezone
    onUpdate: 'resubscribeDailyStats',
  },

  // === SPLIT INTO MULTIPLE ATOMS ===
  users: {
    table: 'users',
    target: {
      type: 'split',
      splitBy: 'active',
      targets: {
        true: 'setUsersDict',
        false: 'setInactiveUsers',
      },
    },
  },

  // === DIFFERENT FILTER FIELD ===
  organization: {
    table: 'organizations',
    target: { type: 'atom', setter: 'setOrganization' },
    filterField: 'firestore_id',
  },

  // === SIMPLE CONSTANTS SUBSCRIPTIONS ===
  paymentMethods: {
    table: 'payment_methods',
    target: { type: 'constants', key: 'paymentMethods' },
  },

  purchaseMethods: {
    table: 'purchase_methods',
    target: { type: 'constants', key: 'purchaseMethods' },
  },

  wallets: {
    table: 'wallets',
    target: { type: 'constants', key: 'wallets' },
  },

  priceTypes: {
    table: 'price_types',
    target: { type: 'constants', key: 'priceTypes' },
  },

  writeOffReasons: {
    table: 'write_off_reasons',
    target: { type: 'constants', key: 'writeOffReasons' },
  },

  returnReasons: {
    table: 'return_reasons',
    target: { type: 'constants', key: 'returnReasons' },
  },

  recurringSpendings: {
    table: 'recurring_spendings',
    target: { type: 'constants', key: 'recurringSpendings' },
  },

  // === CONSTANTS WITH DICT FORMAT ===
  saleSettings: {
    table: 'sale_settings',
    target: { type: 'constants', key: 'saleSettings', asDict: true },
    deleteKey: 'storeId',
  },

  // === SPLIT INTO MULTIPLE CONSTANTS KEYS ===
  spendingCategories: {
    table: 'spending_categories',
    target: {
      type: 'constantsSplit',
      splitBy: 'isAdmin',
      keys: {
        false: 'spendingCategories',
        true: 'adminSpendingCategories',
      },
    },
  },

  // === LOYALTY SETTINGS (single document, spreads fields directly) ===
  loyaltySettings: {
    table: 'loyalty_settings',
    target: { type: 'constantsSingle', key: 'loyaltySettings' },
  },

  // === COMPLEX TRANSFORM (i18n) ===
  discountOptions: {
    table: 'discount_options',
    target: { type: 'atom', setter: 'setDiscountOptionsList' },
    transformKey: 'discountOptions',
  },

  // === COMPLEX SALE TEMPLATES ===
  complexSaleTemplates: {
    table: 'complex_sale_templates',
    target: { type: 'constants', key: 'complexSaleTemplates' },
  },

  // Print templates. Refetches the list, like every other entity.
  //
  // This was `clearOnly`, which set `printTemplatesAtom` to `[]` on any change and relied on a
  // "lazy load" that did not exist: the only fetcher was never called from anywhere, and the
  // Supabase boot path did not hydrate the atom either. The result was that a cut-over
  // organization printed invoices with the BUILT-IN layout instead of its own — silently, because
  // `getPrintTemplateSettings()` returns `null` for an empty atom and `generateExpenseInvoice`
  // treats `null` as "use defaults". Same shape as ATL-1038's `printChecks`.
  printTemplates: {
    table: 'print_templates',
    target: { type: 'atom', setter: 'setPrintTemplates' },
  },

  // === STOCK UPDATES (triggers product refetch) ===
  productStock: {
    table: 'product_store_stock',
    target: { type: 'atom', setter: 'setProductsDict' }, // Used by scheduleDebouncedProductRefetch helper
    debounce: 500, // Fast debounce for stock updates (faster than products' 2000ms)
    fetchSingle: true,
    triggerProductRefetch: true, // Triggers product refetch on stock change instead of direct update
  },
};

/**
 * Get table name from config key (converts camelCase to snake_case)
 */
export function getTableName(key: string, config: SubscriptionConfig): TableName {
  if (config.table) {
    return config.table;
  }
  // Convert camelCase to snake_case
  return key.replace(/([A-Z])/g, '_$1').toLowerCase() as TableName;
}

/**
 * Get all subscription config entries as array
 */
export function getSubscriptionEntries(): Array<[string, SubscriptionConfig]> {
  return Object.entries(SUBSCRIPTION_CONFIGS);
}

export default SUBSCRIPTION_CONFIGS;
